// src/frontend/src/pages/TermsPage.js
import { Box, Container, Typography, Paper } from '@mui/material';

const TermsPage = () => {
    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
                <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
                    Terms of Service
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Last updated: September 19, 2026
                </Typography>

                <Box sx={{ '& h2': { mt: 4, mb: 2 }, '& p': { mb: 2, lineHeight: 1.8 } }}>
                    <Typography variant="h5" component="h2" fontWeight="600">
                        1. Agreement to Terms
                    </Typography>
                    <Typography variant="body1">
                        By accessing or using StockMaid ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
                    </Typography>
                    <Typography variant="body1">
                        These Terms apply to all visitors, users, and others who access or use the Service.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        2. Description of Service
                    </Typography>
                    <Typography variant="body1">
                        StockMaid is a product availability tracking service that monitors stock levels at retailers (primarily Best Buy) and notifies users when products become available. The Service includes:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Free Discord notifications for product availability</Typography></li>
                        <li><Typography variant="body1">Premium subscription features including personalized email alerts, state-specific in-store availability tracking, and advanced notification preferences</Typography></li>
                    </Box>
                    <Typography variant="body1">
                        <strong>Important:</strong> StockMaid is an informational service only. We do not sell products, guarantee product availability, or make purchases on behalf of users. We aggregate publicly available information from retailer websites.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        3. User Accounts
                    </Typography>
                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        3.1 Account Creation
                    </Typography>
                    <Typography variant="body1">
                        To access certain features, you must create an account using Google Sign-In. You agree to provide accurate information and keep your account secure.
                    </Typography>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        3.2 Account Responsibilities
                    </Typography>
                    <Typography variant="body1">
                        You are responsible for:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Maintaining the confidentiality of your account</Typography></li>
                        <li><Typography variant="body1">All activities that occur under your account</Typography></li>
                        <li><Typography variant="body1">Notifying us immediately of any unauthorized use</Typography></li>
                    </Box>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        3.3 Account Termination
                    </Typography>
                    <Typography variant="body1">
                        We reserve the right to suspend or terminate your account at any time for violations of these Terms, abuse of the Service, or any other reason at our discretion.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        4. Premium Subscription
                    </Typography>
                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        4.1 Subscription Terms
                    </Typography>
                    <Typography variant="body1">
                        New Premium subscriptions are billed monthly at $9.99 USD. Subscriptions started before September 19, 2026 keep the monthly price they started at for as long as they remain active. By subscribing, you authorize us to charge your payment method on a recurring basis until you cancel.
                    </Typography>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        4.2 Free Trial
                    </Typography>
                    <Typography variant="body1">
                        New users may start a 3-day free trial. A payment method is required to start the trial. You will not be charged during the trial; when it ends, your subscription begins and your payment method is charged $9.99 USD per month unless you cancel before the trial ends. Free trials are limited to one per person/household. If you have previously subscribed or used a free trial, including the one-month offer available before September 19, 2026, you are not eligible for another free trial.
                    </Typography>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        4.3 Cancellation
                    </Typography>
                    <Typography variant="body1">
                        You may cancel your subscription at any time through your account settings. Upon cancellation, you will retain access to premium features until the end of your current billing period.
                    </Typography>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        4.4 Price Changes
                    </Typography>
                    <Typography variant="body1">
                        We reserve the right to change subscription prices. Price changes will be communicated in advance and will not affect your current billing period.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        5. Acceptable Use
                    </Typography>
                    <Typography variant="body1">
                        You agree not to:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Use the Service for any illegal purpose</Typography></li>
                        <li><Typography variant="body1">Attempt to gain unauthorized access to any part of the Service</Typography></li>
                        <li><Typography variant="body1">Use automated systems (bots, scrapers) to access the Service excessively</Typography></li>
                        <li><Typography variant="body1">Interfere with or disrupt the Service or servers</Typography></li>
                        <li><Typography variant="body1">Resell, redistribute, or commercially exploit the Service without permission</Typography></li>
                        <li><Typography variant="body1">Circumvent rate limits or other technical restrictions</Typography></li>
                        <li><Typography variant="body1">Share your account credentials with others</Typography></li>
                        <li><Typography variant="body1">Use the Service to engage in scalping or reselling products at inflated prices</Typography></li>
                    </Box>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        6. Intellectual Property
                    </Typography>
                    <Typography variant="body1">
                        The Service and its original content (excluding user-provided content), features, and functionality are owned by StockMaid and are protected by copyright, trademark, and other intellectual property laws.
                    </Typography>
                    <Typography variant="body1">
                        Product names, logos, and brands displayed on the Service are property of their respective owners. StockMaid is not affiliated with Best Buy, NVIDIA, or any GPU manufacturer.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        7. Disclaimers
                    </Typography>
                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        7.1 Service Availability
                    </Typography>
                    <Typography variant="body1">
                        The Service is provided "as is" and "as available" without warranties of any kind. We do not guarantee:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Uninterrupted or error-free operation</Typography></li>
                        <li><Typography variant="body1">Accuracy or completeness of stock information</Typography></li>
                        <li><Typography variant="body1">That notifications will be delivered in time to purchase products</Typography></li>
                        <li><Typography variant="body1">That products will still be available after notification</Typography></li>
                    </Box>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        7.2 Third-Party Information
                    </Typography>
                    <Typography variant="body1">
                        Stock availability information is aggregated from third-party retailers. We are not responsible for inaccuracies in retailer data, changes in retailer policies, or retailer website downtime.
                    </Typography>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        7.3 No Purchase Guarantee
                    </Typography>
                    <Typography variant="body1">
                        Receiving a notification does not guarantee your ability to purchase a product. Stock may sell out, retailers may impose purchase limits, and other factors may prevent successful purchases.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        8. Limitation of Liability
                    </Typography>
                    <Typography variant="body1">
                        To the maximum extent permitted by law, StockMaid and its operators shall not be liable for:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Any indirect, incidental, special, consequential, or punitive damages</Typography></li>
                        <li><Typography variant="body1">Loss of profits, data, or goodwill</Typography></li>
                        <li><Typography variant="body1">Cost of procurement of substitute services</Typography></li>
                        <li><Typography variant="body1">Any damages resulting from missed product purchases</Typography></li>
                    </Box>
                    <Typography variant="body1">
                        Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        9. Indemnification
                    </Typography>
                    <Typography variant="body1">
                        You agree to defend, indemnify, and hold harmless StockMaid and its operators from any claims, damages, losses, or expenses arising from your use of the Service or violation of these Terms.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        10. Modifications to Service
                    </Typography>
                    <Typography variant="body1">
                        We reserve the right to modify, suspend, or discontinue any part of the Service at any time without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuation.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        11. Changes to Terms
                    </Typography>
                    <Typography variant="body1">
                        We may revise these Terms at any time. Material changes will be notified via email or through the Service. Continued use after changes constitutes acceptance of the new Terms.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        12. Governing Law
                    </Typography>
                    <Typography variant="body1">
                        These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law provisions. Any disputes shall be resolved in the courts of the applicable jurisdiction.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        13. Severability
                    </Typography>
                    <Typography variant="body1">
                        If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        14. Contact Us
                    </Typography>
                    <Typography variant="body1">
                        If you have any questions about these Terms, please contact us:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Email: support@stockmaid.com</Typography></li>
                        <li><Typography variant="body1">Discord: discord.gg/W78pdPCdbC</Typography></li>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

export default TermsPage;